import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthResponse, UserRole } from '../types';
import { apiService } from '../services/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  selectedOrganizationId: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => Promise<void>;
  setSelectedOrganization: (organizationId: string | null) => void;
  isAuthenticated: boolean;
  hasRole: (roles: string[]) => boolean;
  canAccessProject: (projectId: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(() => {
    // Initialize from localStorage if available
    return localStorage.getItem('selectedOrganizationId');
  });

  // Check for existing session on mount
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      setLoading(true);
      console.log('🚀 Initializing authentication...');
      
      // First, check if we have a token from the parent time tracker window
      const urlParams = new URLSearchParams(window.location.search);
      const parentToken = urlParams.get('token');
      
      if (parentToken) {
        console.log('📥 Token found in URL params');
        // Token passed from parent window (time tracker)
        localStorage.setItem('authToken', parentToken);
        await loadUserFromToken(parentToken);
      } else {
        // Check localStorage for existing token
        const storedToken = localStorage.getItem('authToken');
        if (storedToken) {
          console.log('📥 Token found in localStorage');
          await loadUserFromToken(storedToken);
        } else {
          console.log('❌ No token found');
        }
      }
    } catch (error) {
      console.error('❌ Auth initialization failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const loadUserFromToken = async (authToken: string) => {
    const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));
    const tryLoad = async (attempt: number): Promise<void> => {
      try {
        console.log('🔄 Loading user from token...');
        console.log('🔄 Token:', authToken ? 'Present' : 'Missing');

        // Set token for API calls
        setToken(authToken);

        // Fetch user data
        console.log('🔄 Calling getCurrentUser...');
        const userData = await apiService.getCurrentUser();
        console.log('✅ User loaded from token:', userData.email);
        console.log('✅ User data:', userData);
        setUser(userData);

        // Notify parent window of successful authentication (if embedded)
        if (window.parent !== window) {
          window.parent.postMessage({
            type: 'AGILE_PM_AUTH_SUCCESS',
            user: userData
          }, '*');
        }
      } catch (error: any) {
        const isNetworkError = !error?.response && (error?.code === 'ERR_NETWORK' || /Network Error/i.test(error?.message || '') || /ECONNRESET|CONNECTION_RESET/i.test(String(error)));
        if (isNetworkError && attempt < 2) {
          console.warn(`🔁 Retrying getCurrentUser after transient network error (attempt ${attempt + 1})...`);
          await sleep(800);
          return tryLoad(attempt + 1);
        }
        console.error('❌ Failed to load user from token:', error);
        console.error('❌ Error details:', error?.response?.data || error?.message);
        throw error;
      }
    };
    return tryLoad(0);
  };

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setLoading(true);
      console.log('🔐 Starting login process for:', email);
      
      const authResponse: AuthResponse = await apiService.login(email, password);
      console.log('✅ Login successful, user:', authResponse.user.email);
      console.log('✅ Token received:', authResponse.token ? 'Yes' : 'No');
      
      setUser(authResponse.user);
      setToken(authResponse.token);
      localStorage.setItem('authToken', authResponse.token);
      console.log('✅ Token stored in localStorage');
      
      // Notify parent window of authentication
      if (window.parent !== window) {
        window.parent.postMessage({
          type: 'AGILE_PM_AUTH_SUCCESS',
          user: authResponse.user,
          token: authResponse.token
        }, '*');
      }
      // Note: Navigation will be handled by PrivateRoute redirecting to dashboard
    } catch (error) {
      console.error('❌ Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loginWithToken = async (authToken: string): Promise<void> => {
    try {
      setLoading(true);
      localStorage.setItem('authToken', authToken);
      await loadUserFromToken(authToken);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setSelectedOrganizationId(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('selectedOrganizationId');
    
    // Notify parent window of logout
    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'AGILE_PM_AUTH_LOGOUT'
      }, '*');
    }
  };

  const setSelectedOrganization = (organizationId: string | null) => {
    setSelectedOrganizationId(organizationId);
    if (organizationId) {
      localStorage.setItem('selectedOrganizationId', organizationId);
    } else {
      localStorage.removeItem('selectedOrganizationId');
    }
  };

  const updateUser = async (userData: Partial<User>): Promise<void> => {
    try {
      const updatedUser = await apiService.updateProfile(userData);
      setUser(updatedUser);
    } catch (error) {
      throw error;
    }
  };

  const hasRole = (roles: string[]): boolean => {
    return user ? roles.includes(user.role) : false;
  };

  const canAccessProject = (projectId: string): boolean => {
    if (!user) return false;
    
    // Admins can access all projects
    if ([UserRole.ADMIN, UserRole.ROOT_USER, UserRole.SUPER_MANAGER].includes(user.role)) {
      return true;
    }
    
    // Check team membership
    return user.teamMemberships?.some(
      membership => 
        membership.isActive && 
        membership.team?.project?.id === projectId
    ) || false;
  };

  const value = {
    user,
    token,
    loading,
    selectedOrganizationId,
    login,
    loginWithToken,
    logout,
    updateUser,
    setSelectedOrganization,
    isAuthenticated: !!user && !!token,
    hasRole,
    canAccessProject
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};